<template>
  <div
    :class="[
      'UIInputCheckbox',
      {
        'UIInputCheckbox--has-errors': hasErrors,
        'UIInputCheckbox--is-disabled': isDisabled,
      },
      $attrs.class,
    ]"
  >
    <UIInputLabel
      class="UIInputCheckbox__label"
      :for="name"
      :data-cy="`input[${name}]`"
    >
      {{ label }}
      <input
        :id="name"
        class="UIInputCheckbox__input"
        type="checkbox"
        v-bind="$attrs"
        :checked="currentCheckedValue"
        :value="value"
        :disabled="isDisabled"
        @input="emitValue"
        @keydown.enter.prevent=""
      />

      <span class="UIInputCheckbox__checkmark" />
    </UIInputLabel>

    <template v-for="error in errors" :key="error">
      <div class="UIInputCheckbox__error">
        {{ error }}
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { toRef, computed, watch } from 'vue'

import { useField } from 'vee-validate'

import UIInputLabel from './UIInputLabel.vue'

defineOptions({
  inheritAttrs: false,
})
const props = withDefaults(
  defineProps<{
    label?: string
    name: string
    modelValue?: boolean
    isDisabled?: boolean
  }>(),
  {
    modelValue: undefined,
    isDisabled: false,
  },
)
const emit = defineEmits(['change', 'input', 'update:modelValue'])

const emitValue = (event: Event) => {
  const checked = (event.target as HTMLInputElement).checked
  // Update vee-validate field value (syncVModel will handle modelValue sync)
  value.value = checked

  // Emit events
  emit('input', checked) // Emit checkbox state
  emit('update:modelValue', checked) // Emit checkbox state for v-model
  emit('change', checked) // Always emit change event for @change handlers
}

const hasErrors = computed(() => errors.value && errors.value.length)
const { value, errors } = useField(toRef(props, 'name'), {})
watch(
  value,
  (newValue) => {
    emit('update:modelValue', newValue)
  },
  { immediate: true },
)
watch(
  () => props.modelValue,
  (newModelValue) => {
    if (newModelValue === undefined) return
    value.value = newModelValue
  },
  { immediate: true },
)
// Use vee-validate field value for checked state
const currentCheckedValue = computed(() => !!value.value)
</script>

<style scoped lang="scss">
.UIInputCheckbox {
  position: relative;
  display: flex;
  // height: 1rem;
  align-items: center;
  padding-left: 1.5rem;
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  --border-color: #{$color-neutral-40};
  --background-color: #{$color-white};
  --check-color: #{$color-white};

  &.UIInputCheckbox--has-errors {
    --border-color: #{$color-error-40};
    &:hover {
      --border-color: #{$color-error-40};
    }

    .UIInputCheckbox__input:checked ~ .UIInputCheckbox__checkmark {
      --border-color: #{$color-error-40};
      --background-color: #{$color-error-40};
      --check-color: #{$color-white};

      &:hover {
        --border-color: #{$color-error-40};
        --background-color: #{$color-error-40};
        --check-color: #{$color-white};
      }
    }
  }

  &.UIInputCheckbox--is-disabled {
    pointer-events: none;
    --border-color: #{$color-neutral-20};

    .UIInputCheckbox__input:checked ~ .UIInputCheckbox__checkmark {
      --background-color: #{$color-neutral-10};
      --border-color: #{$color-neutral-20};
      --check-color: #{$color-neutral-50};
    }
  }

  &:hover {
    --border-color: #{$color-neutral-80};
  }
  &:active {
    --border-color: #{$color-primary-40};
  }

  .UIInputCheckbox__label {
    cursor: pointer;
    margin-bottom: 0;
    @include font-body-small-medium;
  }

  .UIInputCheckbox__input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;

    &:checked ~ .UIInputCheckbox__checkmark {
      --background-color: #{$color-primary-40};
      --border-color: #{$color-primary-40};

      &:hover {
        --background-color: #{$color-primary-50};
        --border-color: #{$color-primary-50};
      }
      &:active {
        --background-color: #{$color-primary-60};
        --border-color: #{$color-primary-60};
      }

      &::after {
        display: block;
      }
    }
  }

  .UIInputCheckbox__checkmark {
    position: absolute;
    top: calc(50% - 0.5rem);
    left: 0;
    height: 1rem;
    width: 1rem;
    cursor: pointer;
    border-radius: 4px;
    border: 1px solid;
    border-color: var(--border-color);
    background-color: var(--background-color);

    &::after {
      content: '';
      position: absolute;
      display: none;
      left: 5px;
      top: 1px;
      width: 0.3125rem;
      height: 0.5625rem;
      border: solid var(--check-color);
      border-width: 0 1px 1px 0;
      -webkit-transform: rotate(45deg);
      -ms-transform: rotate(45deg);
      transform: rotate(45deg);
    }
  }
  .UIInputCheckbox__error {
    @include font-size-10px;
    color: $color-error-40;
    margin-top: 0.25rem;
  }
}
</style>
